import type { InspectedObject } from '../model/BoardObjectInspector';
import { REGIONS, type RegionId, type RegionState, type StateMap } from '../types';

export interface HUDCallbacks {
  selectRegion: (region: RegionId) => void;
  resetView: () => void;
  startDemo: () => void;
  pauseDemo: () => void;
  resetDemo: () => void;
  toggleDebug: () => void;
}

export class HUD {
  private active: RegionId = 'AND';
  private readonly root: HTMLElement;
  private readonly panel: HTMLElement;
  private readonly status: HTMLElement;
  private readonly loading: HTMLElement;
  private readonly debug: HTMLElement;

  constructor(host: HTMLElement, callbacks: HUDCallbacks) {
    host.insertAdjacentHTML('beforeend', this.template());
    this.root = host.querySelector('.hud')!;
    this.panel = host.querySelector('#lesson-panel')!;
    this.status = host.querySelector('#status-line')!;
    this.loading = host.querySelector('#loading-screen')!;
    this.debug = host.querySelector('#debug-panel')!;
    this.root.addEventListener('click', (event) => {
      const button = (event.target as HTMLElement).closest<HTMLButtonElement>('button[data-action]');
      if (!button) return;
      const action = button.dataset.action;
      if (action === 'region') callbacks.selectRegion(button.dataset.region as RegionId);
      if (action === 'reset-view') callbacks.resetView();
      if (action === 'demo-start') callbacks.startDemo();
      if (action === 'demo-pause') callbacks.pauseDemo();
      if (action === 'demo-reset') callbacks.resetDemo();
      if (action === 'debug') callbacks.toggleDebug();
    });
  }

  setReady(): void {
    this.loading.classList.add('is-hidden');
  }

  setLoadingError(message: string): void {
    this.loading.innerHTML = `<strong>模型加载失败</strong><span>${message}</span>`;
  }

  update(states: StateMap, active = this.active): void {
    this.active = active;
    const definition = REGIONS.find((item) => item.id === active)!;
    const state = states[active];
    document.querySelectorAll<HTMLButtonElement>('.region-tab').forEach((tab) => {
      tab.classList.toggle('is-active', tab.dataset.region === active);
    });
    this.panel.innerHTML = `
      <div class="panel-kicker">${definition.chip} · ${definition.id}</div>
      <div class="panel-title-row"><h2>${definition.title}</h2><code>${definition.formula}</code></div>
      <div class="logic-readout">
        <div><span>INPUT A</span><b class="${state.a ? 'high' : ''}">${+state.a}</b></div>
        ${active === 'NOT' ? '' : `<div><span>INPUT B</span><b class="${state.b ? 'high' : ''}">${+state.b}</b></div>`}
        <div class="output"><span>OUTPUT Y</span><b class="${state.output ? 'high' : ''}">${+state.output}</b></div>
        <div><span>LED</span><b class="${state.output ? 'led-on' : ''}">${state.output ? '亮' : '灭'}</b></div>
      </div>
      <p class="explanation">${this.explanation(active, state)}</p>
      ${this.truthTable(active, state)}
      <p class="hint">点击板上的拨动开关改变输入；LED 为高电平点亮。</p>
    `;
  }

  setStatus(text: string): void {
    this.status.textContent = text;
  }

  showDebug(show: boolean): void {
    this.debug.classList.toggle('is-open', show);
  }

  setDebug(objects: InspectedObject[], mapping: string, hit = '尚未点击对象'): void {
    const rows = objects.slice(0, 90).map((item) =>
      `<tr><td>${item.name}</td><td>${item.type}</td><td>${item.min}</td><td>${item.max}</td></tr>`,
    ).join('');
    this.debug.innerHTML = `
      <div class="debug-head"><div><b>Developer Debug</b><span>${objects.length} objects · ${mapping}</span></div><button data-action="debug">关闭</button></div>
      <p id="debug-hit">${hit}</p>
      <div class="debug-scroll"><table><thead><tr><th>Name</th><th>Type</th><th>Min</th><th>Max</th></tr></thead><tbody>${rows}</tbody></table></div>
    `;
  }

  setDebugHit(text: string): void {
    const target = this.debug.querySelector('#debug-hit');
    if (target) target.textContent = text;
  }

  private truthTable(id: RegionId, state: RegionState): string {
    const rows: Array<[number, number, number]> = id === 'NOT'
      ? [[0, 0, 1], [1, 0, 0]]
      : [[0, 0, this.output(id, 0, 0)], [0, 1, this.output(id, 0, 1)], [1, 0, this.output(id, 1, 0)], [1, 1, this.output(id, 1, 1)]];
    return `<table class="truth-table"><thead><tr><th>A</th>${id === 'NOT' ? '' : '<th>B</th>'}<th>Y</th><th>LED</th></tr></thead><tbody>${rows.map(([a, b, y]) => {
      const current = a === +state.a && (id === 'NOT' || b === +state.b);
      return `<tr class="${current ? 'current' : ''}"><td>${a}</td>${id === 'NOT' ? '' : `<td>${b}</td>`}<td>${y}</td><td>${y ? '●' : '○'}</td></tr>`;
    }).join('')}</tbody></table>`;
  }

  private output(id: RegionId, a: number, b: number): number {
    if (id === 'AND') return +(!!a && !!b);
    if (id === 'OR') return +(!!a || !!b);
    if (id === 'NAND') return +!(!!a && !!b);
    return +!a;
  }

  private explanation(id: RegionId, state: RegionState): string {
    const a = +state.a;
    const b = +state.b;
    const y = +state.output;
    if (id === 'NOT') return `输入 A=${a}，非门将输入反相，所以输出 Y=${y}。`;
    const labels = { AND: '两个输入都为 1 时才输出 1', OR: '任一输入为 1 就输出 1', NAND: '仅当两个输入都为 1 时输出 0' };
    return `A=${a}，B=${b}。${labels[id]}，当前 Y=${y}。`;
  }

  private template(): string {
    return `
      <div id="loading-screen"><span class="loader"></span><strong>正在装载 KiCad PCB</strong><small>4.09 MB industrial GLB</small></div>
      <div class="hud">
        <header class="topbar">
          <div class="brand"><span class="brand-mark">01</span><div><strong>数字逻辑实验台（苏宇彤设计）</strong><small>INTERACTIVE LOGIC LAB</small></div></div>
          <div class="top-actions">
            <button data-action="reset-view">复位视角</button>
            <button data-action="debug">调试</button>
          </div>
        </header>
        <nav class="region-tabs">${REGIONS.map((r) => `<button class="region-tab ${r.id === 'AND' ? 'is-active' : ''}" data-action="region" data-region="${r.id}"><span>${r.id}</span>${r.title}</button>`).join('')}</nav>
        <aside id="lesson-panel"></aside>
        <div class="demo-bar">
          <span>辅助演示</span>
          <button data-action="demo-start">▶ 开始</button>
          <button data-action="demo-pause">Ⅱ 暂停 / 继续</button>
          <button data-action="demo-reset">↺ 重置</button>
        </div>
        <div id="status-line">左键拖动旋转 · 中键拖动平移 · 滚轮缩放 · 点击白色拨动开关</div>
        <section id="debug-panel"></section>
      </div>
    `;
  }
}
